import {useMutation, useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {activateRecord, cancelRecord} from '../services/CustomerSubscriptionsService';
import {CustomerSubscription, CustomerSubscriptionStatus} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications.tsx';
import {customerSubscriptionsQueryKeys} from './customerSubscriptionsQueryKeys';

// The row Activate/Cancel action. Same two API calls as before (activate and cancel by
// id, nothing else sent); it is a mutation rather than a raw service call so a rejection
// is shown instead of becoming an unhandled promise, the button shows it is pending (§2:
// no dead click), and the invalidation reaches the detail key as well as the list (§8).
export const useSubscriptionStatusChange = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (record: CustomerSubscription) =>
            record.status === CustomerSubscriptionStatus.Active ? cancelRecord(record.id) : activateRecord(record.id),
        onSuccess: (_result, record) => {
            notifySuccess(record.status === CustomerSubscriptionStatus.Active
                              ? I18n.get('Subscription cancelled')
                              : I18n.get('Subscription activated'));
        },
        onError: (error) => notifyError(error),
        onSettled: () => queryClient.invalidateQueries({queryKey: customerSubscriptionsQueryKeys.all}),
    });

    return {
        changeStatus: (record: CustomerSubscription) => mutation.mutate(record),
        // The id in flight, so only that row's button shows the spinner.
        pendingId: mutation.isPending ? mutation.variables?.id : undefined,
    };
};
